/**
 * SDK Translations
 *
 * Translations for all fixed UI texts in the SDK
 */

export interface Translations {
    whatsNew: string;
    readMore: string;
    close: string;
    back: string;
    noAnnouncements: string;
    loading: string;
    listError: string;
    page: string;
    of: string;
    previous: string;
    next: string;
    today: string;
    yesterday: string;
    daysAgo: string; // Template with {days} placeholder
    // Optional full "page X of Y" template with {current}/{last} placeholders,
    // for languages where the label can't be composed as `page N of M` around
    // bare `page`/`of` words (e.g. Chinese needs 第 N 页，共 M 页). When absent,
    // the list falls back to `${page} ${current} ${of} ${last}` so every other
    // locale's rendered output is unchanged.
    pageOf?: string;
    announcementTypes: {
        post: string;
        new_version: string;
        new_feature: string;
        improvement: string;
        update: string;
        bug_fix: string;
        maintenance: string;
        issue: string;
        news: string;
        event: string;
        tutorial: string;
        tips: string;
        deprecation: string;
        survey: string;
        promotion: string;
    };
}

export const translations: Record<string, Translations> = {
    // English
    en: {
        whatsNew: "What's New",
        readMore: 'Read more',
        close: 'Close',
        back: 'Back',
        noAnnouncements: 'No announcements available',
        loading: 'Loading...',
        listError: 'Something went wrong. Please try again.',
        page: 'Page',
        of: 'of',
        previous: 'Previous',
        next: 'Next',
        today: 'Today',
        yesterday: 'Yesterday',
        daysAgo: '{days} days ago',
        announcementTypes: {
            post: 'Post',
            new_version: 'New Version',
            new_feature: 'New Feature',
            improvement: 'Improvement',
            update: 'Update',
            bug_fix: 'Bug Fix',
            maintenance: 'Maintenance',
            issue: 'Issue',
            news: 'News',
            event: 'Event',
            tutorial: 'Tutorial',
            tips: 'Tips',
            deprecation: 'Deprecation',
            survey: 'Survey',
            promotion: 'Promotion',
        },
    },

    // Spanish
    es: {
        whatsNew: 'Novedades',
        readMore: 'Leer más',
        close: 'Cerrar',
        back: 'Volver',
        noAnnouncements: 'No hay anuncios disponibles',
        loading: 'Cargando...',
        listError: 'Algo salió mal. Inténtalo de nuevo.',
        page: 'Página',
        of: 'de',
        previous: 'Anterior',
        next: 'Siguiente',
        today: 'Hoy',
        yesterday: 'Ayer',
        daysAgo: 'hace {days} días',
        announcementTypes: {
            post: 'Publicación',
            new_version: 'Nueva Versión',
            new_feature: 'Nueva Función',
            improvement: 'Mejora',
            update: 'Actualización',
            bug_fix: 'Corrección de Error',
            maintenance: 'Mantenimiento',
            issue: 'Incidencia',
            news: 'Noticias',
            event: 'Evento',
            tutorial: 'Tutorial',
            tips: 'Consejos',
            deprecation: 'Obsolescencia',
            survey: 'Encuesta',
            promotion: 'Promoción',
        },
    },

    // French
    fr: {
        whatsNew: 'Nouveautés',
        readMore: 'Lire la suite',
        close: 'Fermer',
        back: 'Retour',
        noAnnouncements: 'Aucune annonce disponible',
        loading: 'Chargement...',
        listError: 'Une erreur est survenue. Veuillez réessayer.',
        page: 'Page',
        of: 'sur',
        previous: 'Précédent',
        next: 'Suivant',
        today: "Aujourd'hui",
        yesterday: 'Hier',
        daysAgo: 'il y a {days} jours',
        announcementTypes: {
            post: 'Article',
            new_version: 'Nouvelle Version',
            new_feature: 'Nouvelle Fonctionnalité',
            improvement: 'Amélioration',
            update: 'Mise à jour',
            bug_fix: 'Correction de Bug',
            maintenance: 'Maintenance',
            issue: 'Incident',
            news: 'Actualités',
            event: 'Événement',
            tutorial: 'Tutoriel',
            tips: 'Conseils',
            deprecation: 'Dépréciation',
            survey: 'Sondage',
            promotion: 'Promotion',
        },
    },

    // German
    de: {
        whatsNew: 'Was ist neu',
        readMore: 'Mehr lesen',
        close: 'Schließen',
        back: 'Zurück',
        noAnnouncements: 'Keine Ankündigungen verfügbar',
        loading: 'Lädt...',
        listError: 'Etwas ist schiefgelaufen. Bitte versuche es erneut.',
        page: 'Seite',
        of: 'von',
        previous: 'Zurück',
        next: 'Weiter',
        today: 'Heute',
        yesterday: 'Gestern',
        daysAgo: 'vor {days} Tagen',
        announcementTypes: {
            post: 'Beitrag',
            new_version: 'Neue Version',
            new_feature: 'Neue Funktion',
            improvement: 'Verbesserung',
            update: 'Aktualisierung',
            bug_fix: 'Fehlerbehebung',
            maintenance: 'Wartung',
            issue: 'Störung',
            news: 'Nachrichten',
            event: 'Ereignis',
            tutorial: 'Tutorial',
            tips: 'Tipps',
            deprecation: 'Veraltet',
            survey: 'Umfrage',
            promotion: 'Aktion',
        },
    },

    // Portuguese
    pt: {
        whatsNew: 'Novidades',
        readMore: 'Leia mais',
        close: 'Fechar',
        back: 'Voltar',
        noAnnouncements: 'Nenhum anúncio disponível',
        loading: 'Carregando...',
        listError: 'Algo deu errado. Tente novamente.',
        page: 'Página',
        of: 'de',
        previous: 'Anterior',
        next: 'Próximo',
        today: 'Hoje',
        yesterday: 'Ontem',
        daysAgo: 'há {days} dias',
        announcementTypes: {
            post: 'Publicação',
            new_version: 'Nova Versão',
            new_feature: 'Novo Recurso',
            improvement: 'Melhoria',
            update: 'Atualização',
            bug_fix: 'Correção de Bug',
            maintenance: 'Manutenção',
            issue: 'Incidente',
            news: 'Notícias',
            event: 'Evento',
            tutorial: 'Tutorial',
            tips: 'Dicas',
            deprecation: 'Obsoleto',
            survey: 'Pesquisa',
            promotion: 'Promoção',
        },
    },

    // Italian
    it: {
        whatsNew: 'Novità',
        readMore: 'Leggi di più',
        close: 'Chiudi',
        back: 'Indietro',
        noAnnouncements: 'Nessun annuncio disponibile',
        loading: 'Caricamento...',
        listError: 'Qualcosa è andato storto. Riprova.',
        page: 'Pagina',
        of: 'di',
        previous: 'Precedente',
        next: 'Successivo',
        today: 'Oggi',
        yesterday: 'Ieri',
        daysAgo: '{days} giorni fa',
        announcementTypes: {
            post: 'Post',
            new_version: 'Nuova Versione',
            new_feature: 'Nuova Funzionalità',
            improvement: 'Miglioramento',
            update: 'Aggiornamento',
            bug_fix: 'Correzione Bug',
            maintenance: 'Manutenzione',
            issue: 'Problema',
            news: 'Notizie',
            event: 'Evento',
            tutorial: 'Tutorial',
            tips: 'Suggerimenti',
            deprecation: 'Deprecato',
            survey: 'Sondaggio',
            promotion: 'Promozione',
        },
    },

    // Dutch
    nl: {
        whatsNew: 'Wat is nieuw',
        readMore: 'Lees meer',
        close: 'Sluiten',
        back: 'Terug',
        noAnnouncements: 'Geen aankondigingen beschikbaar',
        loading: 'Laden...',
        listError: 'Er is iets misgegaan. Probeer het opnieuw.',
        page: 'Pagina',
        of: 'van',
        previous: 'Vorige',
        next: 'Volgende',
        today: 'Vandaag',
        yesterday: 'Gisteren',
        daysAgo: '{days} dagen geleden',
        announcementTypes: {
            post: 'Bericht',
            new_version: 'Nieuwe Versie',
            new_feature: 'Nieuwe Functie',
            improvement: 'Verbetering',
            update: 'Update',
            bug_fix: 'Bugfix',
            maintenance: 'Onderhoud',
            issue: 'Storing',
            news: 'Nieuws',
            event: 'Evenement',
            tutorial: 'Tutorial',
            tips: 'Tips',
            deprecation: 'Verouderd',
            survey: 'Enquête',
            promotion: 'Promotie',
        },
    },

    // Japanese
    ja: {
        whatsNew: '新機能',
        readMore: '続きを読む',
        close: '閉じる',
        back: '戻る',
        noAnnouncements: 'お知らせはありません',
        loading: '読み込み中...',
        listError: '問題が発生しました。もう一度お試しください。',
        page: 'ページ',
        of: '/',
        previous: '前へ',
        next: '次へ',
        today: '今日',
        yesterday: '昨日',
        daysAgo: '{days}日前',
        announcementTypes: {
            post: '投稿',
            new_version: '新バージョン',
            new_feature: '新機能',
            improvement: '改善',
            update: 'アップデート',
            bug_fix: 'バグ修正',
            maintenance: 'メンテナンス',
            issue: '障害',
            news: 'ニュース',
            event: 'イベント',
            tutorial: 'チュートリアル',
            tips: 'ヒント',
            deprecation: '非推奨',
            survey: 'アンケート',
            promotion: 'プロモーション',
        },
    },

    // Chinese (Simplified)
    zh: {
        whatsNew: '最新动态',
        readMore: '阅读更多',
        close: '关闭',
        back: '返回',
        noAnnouncements: '暂无公告',
        loading: '加载中...',
        listError: '出了点问题，请重试。',
        page: '第',
        of: '页',
        previous: '上一页',
        next: '下一页',
        today: '今天',
        yesterday: '昨天',
        daysAgo: '{days}天前',
        pageOf: '第 {current} 页，共 {last} 页',
        announcementTypes: {
            post: '帖子',
            new_version: '新版本',
            new_feature: '新功能',
            improvement: '改进',
            update: '更新',
            bug_fix: 'Bug修复',
            maintenance: '维护',
            issue: '问题',
            news: '新闻',
            event: '活动',
            tutorial: '教程',
            tips: '提示',
            deprecation: '弃用',
            survey: '调查',
            promotion: '促销',
        },
    },

    // Korean
    ko: {
        whatsNew: '새로운 소식',
        readMore: '더 읽기',
        close: '닫기',
        back: '뒤로',
        noAnnouncements: '공지사항이 없습니다',
        loading: '로딩 중...',
        listError: '문제가 발생했습니다. 다시 시도해 주세요.',
        page: '페이지',
        of: '/',
        previous: '이전',
        next: '다음',
        today: '오늘',
        yesterday: '어제',
        daysAgo: '{days}일 전',
        announcementTypes: {
            post: '게시물',
            new_version: '새 버전',
            new_feature: '새 기능',
            improvement: '개선',
            update: '업데이트',
            bug_fix: '버그 수정',
            maintenance: '유지보수',
            issue: '이슈',
            news: '뉴스',
            event: '이벤트',
            tutorial: '튜토리얼',
            tips: '팁',
            deprecation: '사용 중단',
            survey: '설문조사',
            promotion: '프로모션',
        },
    },

    // Russian
    ru: {
        whatsNew: 'Что нового',
        readMore: 'Читать далее',
        close: 'Закрыть',
        back: 'Назад',
        noAnnouncements: 'Нет доступных объявлений',
        loading: 'Загрузка...',
        listError: 'Что-то пошло не так. Попробуйте снова.',
        page: 'Страница',
        of: 'из',
        previous: 'Назад',
        next: 'Далее',
        today: 'Сегодня',
        yesterday: 'Вчера',
        daysAgo: '{days} дней назад',
        announcementTypes: {
            post: 'Публикация',
            new_version: 'Новая версия',
            new_feature: 'Новая функция',
            improvement: 'Улучшение',
            update: 'Обновление',
            bug_fix: 'Исправление ошибки',
            maintenance: 'Обслуживание',
            issue: 'Проблема',
            news: 'Новости',
            event: 'Событие',
            tutorial: 'Учебник',
            tips: 'Советы',
            deprecation: 'Устарело',
            survey: 'Опрос',
            promotion: 'Акция',
        },
    },

    // Arabic
    ar: {
        whatsNew: 'ما الجديد',
        readMore: 'اقرأ المزيد',
        close: 'إغلاق',
        back: 'رجوع',
        noAnnouncements: 'لا توجد إعلانات متاحة',
        loading: 'جار التحميل...',
        listError: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
        page: 'صفحة',
        of: 'من',
        previous: 'السابق',
        next: 'التالي',
        today: 'اليوم',
        yesterday: 'أمس',
        daysAgo: 'منذ {days} أيام',
        announcementTypes: {
            post: 'منشور',
            new_version: 'نسخة جديدة',
            new_feature: 'ميزة جديدة',
            improvement: 'تحسين',
            update: 'تحديث',
            bug_fix: 'إصلاح خطأ',
            maintenance: 'صيانة',
            issue: 'مشكلة',
            news: 'أخبار',
            event: 'حدث',
            tutorial: 'برنامج تعليمي',
            tips: 'نصائح',
            deprecation: 'إهمال',
            survey: 'استطلاع',
            promotion: 'عرض ترويجي',
        },
    },

    // Hindi
    hi: {
        whatsNew: 'नया क्या है',
        readMore: 'और पढ़ें',
        close: 'बंद करें',
        back: 'वापस',
        noAnnouncements: 'कोई घोषणा उपलब्ध नहीं है',
        loading: 'लोड हो रहा है...',
        listError: 'कुछ गलत हो गया। कृपया पुनः प्रयास करें।',
        page: 'पृष्ठ',
        of: 'का',
        previous: 'पिछला',
        next: 'अगला',
        today: 'आज',
        yesterday: 'कल',
        daysAgo: '{days} दिन पहले',
        announcementTypes: {
            post: 'पोस्ट',
            new_version: 'नया संस्करण',
            new_feature: 'नई सुविधा',
            improvement: 'सुधार',
            update: 'अपडेट',
            bug_fix: 'बग फिक्स',
            maintenance: 'रखरखाव',
            issue: 'समस्या',
            news: 'समाचार',
            event: 'कार्यक्रम',
            tutorial: 'ट्यूटोरियल',
            tips: 'सुझाव',
            deprecation: 'पदावनत',
            survey: 'सर्वेक्षण',
            promotion: 'प्रचार',
        },
    },
};

/**
 * Get translations for a specific language
 * Falls back to English if language not found
 */
export function getTranslations(language: string): Translations {
    // Extract language code (e.g., 'en-US' -> 'en')
    const langCode = language.split('-')[0].toLowerCase();

    // Return translations or fallback to English
    return translations[langCode] || translations.en;
}
